import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const BannerContainer = styled.div`
  width: 100%;
  max-width: 340px;
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const BannerTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
`;

const BannerText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BannerButton = styled.button`
  border: none;
  padding: 8px 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const SecondaryButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 8px 12px;
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
`;

const isIos = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
};

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
};

const AddToHomeScreenBanner: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (dismissed || isStandalone()) {
    return null;
  }

  const showIos = isIos();

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <BannerContainer>
      <BannerTitle>Add to Home Screen</BannerTitle>
      <BannerText>
        {showIos
          ? 'Tap Share in Safari, then “Add to Home Screen” for the full PWA experience.'
          : 'Install the Sentry PWA for a full-screen experience.'}
      </BannerText>
      <BannerActions>
        {!showIos && installPrompt && (
          <BannerButton type="button" onClick={handleInstall}>
            Install
          </BannerButton>
        )}
        <SecondaryButton type="button" onClick={() => setDismissed(true)}>
          Not now
        </SecondaryButton>
      </BannerActions>
    </BannerContainer>
  );
};

export default AddToHomeScreenBanner;
